import React, { useEffect, useState } from 'react';

import { PaletteType, TextPalette } from '../color-v2/types';
import Text from '../text-v2';
import { FontType } from '../text-v2/types';

enum TextInputType {
  normal = 'normal',
  error = 'error',
}

enum TextInputState {
  default = 'default',
  hover = 'hover',
  active = 'active',
  filled = 'filled',
  validate = 'validate',
  disabled = 'disabled',
  error = 'error',
}

interface ITextInputProps {
  label?: string;
  type?: TextInputType;
  bottomText?: string;
  value?: string;
  onChange?: (value: string) => void;
  state?: TextInputState;
  prefixIcon?: React.ReactNode;
  suffixIcon?: React.ReactNode;
  onSuffixIconClick?: () => void;
  initialState?: TextInputState;
  validationRules?: {
    required?: boolean;
    minLength?: number;
    maxLength?: number;
    pattern?: {
      value: RegExp;
      message: string;
    };
  };
  placeholderText: string;
  isDisabled?: boolean;
}

const getInputClassNames = (type: TextInputType, state: TextInputState, isDisabled: boolean) => {
  const baseClass = `border rounded-md px-3 py-2 w-full placeholder:text-text-DARK_3 placeholder:text-sm focus:outline-none focus:border-palette-PRIMARY_500 hover:border-palette-GREYS_600_40 border-radius-2`;

  const stateClass = {
    [TextInputState.default]: 'border-stroke-DARK_3',
    [TextInputState.validate]: 'border-palette-WARNING_600',
    [TextInputState.error]: 'border-palette-WARNING_600',
  }[state];

  const disabledClass = isDisabled ? 'text-text-DARK_3 cursor-not-allowed' : 'text-text-DARK_1';

  return `${baseClass} ${stateClass} ${disabledClass}`;
};

const TextInput: React.FC<ITextInputProps> = ({
  label,
  type = TextInputType.normal,
  bottomText,
  value: propValue = '',
  onChange,
  initialState = TextInputState.default,
  prefixIcon,
  suffixIcon,
  onSuffixIconClick,
  validationRules,
  placeholderText,
  isDisabled,
}) => {
  const [state, setState] = useState<TextInputState>(initialState);
  const [value, setValue] = useState<string>(propValue);
  const [error, setError] = useState<string | undefined>();

  const validateInput = (inputValue: string) => {
    if (!validationRules || inputValue.length === 0) return;
    if (validationRules.required && inputValue.trim() === '') return 'This field is required';
    if (validationRules.minLength && inputValue.length < validationRules.minLength)
      return `Minimum length is ${validationRules.minLength} characters`;
    if (validationRules.maxLength && inputValue.length > validationRules.maxLength)
      return `Maximum length is ${validationRules.maxLength} characters`;
    if (validationRules.pattern && !validationRules.pattern.value.test(inputValue))
      return validationRules.pattern.message;
    return;
  };

  useEffect(() => {
    setValue(propValue);
  }, [propValue]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setValue(newValue);
    onChange && onChange(newValue);
    const validationError = validateInput(newValue);
    setError(validationError);
    setState(validationError ? TextInputState.error : newValue ? TextInputState.filled : TextInputState.default);
  };

  const handleFocus = () => setState(TextInputState.active);
  const handleBlur = () =>
    setState(error ? TextInputState.validate : value ? TextInputState.filled : TextInputState.default);

  const handleInvalidSubmit = (e: React.InvalidEvent<HTMLInputElement>) => {
    e.preventDefault();
    const validationError = validateInput(value);
    setError(validationError);
    setState(TextInputState.error);
  };

  return (
    <div className='flex flex-col gap-1 items-start'>
      {label && (
        <Text font={FontType.SMALL_B} color={isDisabled ? TextPalette.DARK_3 : TextPalette.DARK_1}>
          {label}
        </Text>
      )}
      <div className='relative w-full'>
        {prefixIcon && (
          <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>{prefixIcon}</div>
        )}
        <input
          placeholder={placeholderText}
          value={value}
          required={validationRules?.required}
          minLength={validationRules?.minLength}
          maxLength={validationRules?.maxLength}
          pattern={validationRules?.pattern?.value.source}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onInvalid={handleInvalidSubmit}
          className={`${getInputClassNames(type, state, isDisabled)} ${prefixIcon ? 'pl-10' : ''} ${
            suffixIcon ? 'pr-10' : ''
          }`}
          disabled={isDisabled}
        />
        {suffixIcon && (
          <div className='absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer' onClick={onSuffixIconClick}>
            {suffixIcon}
          </div>
        )}
      </div>
      {(error || bottomText) && (
        <Text
          font={FontType.SMALL_L}
          color={error ? PaletteType.WARNING_700 : isDisabled ? TextPalette.DARK_3 : TextPalette.DARK_2}>
          {error || bottomText}
        </Text>
      )}
    </div>
  );
};

export default TextInput;
